
import { apiSignIn, apiSignOut, apiSignUp } from '@/services/AuthService'
import { getCurrentUserProfile } from '@/services/server/users'
import {
    setUser,
    signInSuccess,
    signOutSuccess,
    useAppSelector,
    useAppDispatch,
} from '@/store'
import appConfig from '@/configs/app.config'
import { REDIRECT_URL_KEY } from '@/constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import type { SignInCredential, SignUpCredential } from '@/types/auth'
import { getAxiosErrorMessage } from '@/utils/axiosErrorMessage'
import { persistor } from '@/store/storeSetup'
import { endGoogleSession, markSilentGoogleAuth } from '@/services/server/fetchers/auth'

type Status = 'success' | 'failed'

function useAuth() {
    const dispatch = useAppDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    const { token, signedIn } = useAppSelector((state) => state.auth.session)

    const mapApiUserToState = (user: Record<string, unknown>, token: string) => ({
        avatar: (user.avatar as string) || '',
        userName: (user.email as string) || '',
        name: (user.name as string) || '',
        authority: ['USER'],
        roles: (user.roles as object[]) || [],
        email: (user.email as string) || '',
        domains: (user.domains as object[]) || [],
        token,
        id: user.id as number,
    })

    const completeGoogleSsoSignIn = async (
        token: string,
        redirectUrl?: string
    ): Promise<{ status: Status; message: string }> => {
        try {
            const resp = await getCurrentUserProfile(token)
            const user = resp.data.user
            dispatch(signInSuccess(token))
            dispatch(setUser(mapApiUserToState(user, token)))
            navigate(redirectUrl || query.get(REDIRECT_URL_KEY) || appConfig.authenticatedEntryPath)
            return { status: 'success', message: '' }
        } catch (errors: unknown) {
            return {
                status: 'failed',
                message: getAxiosErrorMessage(errors, 'Could not complete Google sign-in'),
            }
        }
    }

    const signIn = async ( values: SignInCredential ) : 
      Promise<
        | {
              status: Status
              message: string
          }
        | undefined
    > => {
        try {
            const resp = await apiSignIn(values)
            if (resp.data) {
                const { token } = resp.data.user
                dispatch(signInSuccess(token))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            mapApiUserToState(
                                resp.data.user as Record<string, unknown>,
                                token
                            )
                        )
                    )
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors: unknown) {
            const err = errors as { response?: { data?: { message?: string; response?: { requires2FA?: boolean } } } }
            const data = err?.response?.data
            const payload = data?.response || data
            if (payload && typeof payload === 'object' && 'requires2FA' in payload && payload.requires2FA) {
                return { status: 'failed', message: payload }
            }
            return {
                status: 'failed',
                message: getAxiosErrorMessage(errors, 'Login failed'),
            }
        }
    }

    const signUp = async (values: SignUpCredential) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data) {
                const { token } = (resp.data ?? '');
                dispatch(signInSuccess(token ?? ''))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || {
                                avatar: '',
                                userName: 'Anonymous',
                                authority: ['USER'],
                                email: '',
                            }
                        )
                    )
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }

        } catch (errors: unknown) {
            return {
                status: 'failed',
                message: getAxiosErrorMessage(errors, 'Sign up failed'),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(signOutSuccess())
        dispatch(
            setUser({
                avatar: '', //starter
                userName: 'Anonymous', //starter
                name: '',
                authority: [], //starter
                roles: [],
                email: '',
                domains: [],
                token: '',
            })
        )
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        // await apiSignOut()//for now, we dont need an api call to signout
        // Mark first: handleSignOut opens the sign-in page, and its silent check must not sign us back in.
        markSilentGoogleAuth()
        handleSignOut()
        // Save the signed-out state before the page leaves, then clear the shared login on the backend.
        await persistor.flush()
        endGoogleSession()
    }

    return {
        authenticated: token && signedIn,
        signIn,
        signUp,
        signOut,
        completeGoogleSsoSignIn,
    }
}

export default useAuth
